import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { parseDateSafe } from 'utils/timeHelper';
import {
  startOfDay,
  differenceInCalendarDays,
  intervalToDuration,
  format,
} from 'date-fns';
import common from 'constants/common';
import {
  isRpmModule,
  parseDateStringWithoutTimeZoneAdopt,
} from 'infrastructure/functions';
import { isUndefined } from 'lodash';
import { generateFullName } from 'utils/badgeHelpers';
import { useGetConditions } from 'infrastructure/hooks/ccm';
import { ClinicStatus, LoginStatus, ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
  useSetManualInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';
import { useState, type FC } from 'react';
import { useCheckPatientModule } from 'infrastructure/hooks/patient/use-check-patient-module';

import { useBannerInfo } from './hooks/useBannerInfo';
import CombineMobileBanner from './components/combine-mobile-banner';
import CombinePatientBanner from './components/combine-banner';
import ManualInteractionTimeModalNew from '../interaction-time-modal';
import s from './styles.module.scss';

import type { RpmStatuses } from 'infrastructure/enums';
import type { IRpmPatientAgent } from 'infrastructure/interfaces';

interface CcmRpmPatientBannerProps {
  patientGuid: string;
  careAgents?: IRpmPatientAgent[];
  patientDetailsLoading?: boolean;
}

const CcmRpmPatientBanner: FC<CcmRpmPatientBannerProps> = (props) => {
  const { patientGuid, careAgents, patientDetailsLoading } = props;

  const module = ModuleType.RPM; // for status line

  const {
    birthDate,
    fullName,
    firstName,
    lastName,
    ccmStatus,
    rpmStatus,
    riskScore,
    serviceDeliveryModel,
    providerServiceType,
    isLoading,
    isShowEmailTag,
    sla,
    clinicName,
    physician,
    referralTypes,
    holdingStatus,
    pcmStatus,
    rpmDoNotCall,
    ccmDoNotCall,
    pmpmStatus,
    rpmRestrictionType,
    ccmRestrictionType,
    showSalesForceIntegration,
    disabledCreateInternalAdmissionForms,
    ccmHoldingStatus,
    consumablesArrivingDate,
    isMobileDevice,
    clinicStatus,
    physicianStatus,
    patientTZ,
    apcmStatus,
  } = useBannerInfo({
    patientGuid,
    module: ModuleType.CCM, // CCM module for loading questionnaire
  });

  const [manualInteractionsPopupOpen, setManualInteractionsPopupOpen] =
    useState(false);

  const { patientInfo } = usePatientInfo({ patientGuid });

  const { goals: conditions, isLoading: conditionLoading } = useGetConditions({
    patientGuid,
    meta: { items: 1000, page: 1 },
  });

  const { setManualInteractionTime, isLoading: isSetInteractionTimeLoading } =
    useSetManualInteractionTime({
      onSuccess() {
        setManualInteractionsPopupOpen(false);
      },
    });

  const { clinicReviewTimeSeconds } = useRpmPatientDetails({ patientGuid });
  const today = startOfDay(new Date());
  const { ccmInteractionTime } = useGetCcmInteractionTime({
    patientGuid,
    enabled: true,
  });
  const { rpmInteractionTime } = useGetRpmInteractionTime({
    patientGuid,
    enabled: true,
  });

  const currentMonth = format(new Date(), 'MMMM');

  const { t } = useTranslation();

  const endDate = parseDateStringWithoutTimeZoneAdopt(holdingStatus?.endTime);

  const remainingDays = endDate && differenceInCalendarDays(endDate, today);

  const consumablesEndDate = parseDateSafe(consumablesArrivingDate);
  const consumablesRemainingDays =
    consumablesEndDate &&
    intervalToDuration({ start: today, end: consumablesEndDate })?.days;

  const apcmStatusObj = !isUndefined(apcmStatus)
    ? common.apcmStatus[apcmStatus]
    : undefined;
  const ccmStatusObj = !isUndefined(ccmStatus)
    ? common.ccmStatus[ccmStatus]
    : undefined;
  const rpmStatusObj = !isUndefined(rpmStatus)
    ? common.statusPatients[
        rpmStatus as Exclude<RpmStatuses, RpmStatuses.PatientRefused>
      ]
    : undefined;
  const showHoldingStatusBadge =
    holdingStatus?.status !== 'active' &&
    !isUndefined(holdingStatus?.endTime) &&
    !isUndefined(remainingDays);

  const showTemporaryHoldBadge = ccmHoldingStatus;
  const statusColor = isRpmModule(module)
    ? rpmStatusObj?.color
    : ccmStatusObj?.color;

  const showConsumablesBadge =
    !isUndefined(consumablesArrivingDate) &&
    !isUndefined(consumablesRemainingDays);

  const careAgent = careAgents?.find(({ type }) => type === 'ccmManager');
  const careAgentName = careAgent
    ? generateFullName(
        careAgent.careAgentTitle,
        careAgent.careAgentFirstName,
        '',
        careAgent.careAgentLastName,
        careAgent.careAgentProfession,
      )
    : undefined;

  const { isBlockCcmModule, isBlockRpmModule } =
    useCheckPatientModule(patientGuid);

  const clinicNameElement = (
    <>
      {clinicName}
      {(clinicStatus === ClinicStatus.Delinquent ||
        clinicStatus === ClinicStatus.Inactive) && (
        <span className={s.status}>
          ({t(`clinicStatuses.${clinicStatus}`)})
        </span>
      )}
    </>
  );

  const providerNameElement = (
    <>
      {physician}
      {(physicianStatus === LoginStatus.Inactive ||
        physicianStatus === LoginStatus.Blocked) && (
        <span className={s.status}>
          ({t(`loginStatuses.${physicianStatus}`)})
        </span>
      )}
    </>
  );

  return (
    <>
      {!isMobileDevice && (
        <CombinePatientBanner
          module={ModuleType.RPMCCM}
          patientGuid={patientGuid}
          showConsumablesBadge={showConsumablesBadge}
          showHoldingStatusBadge={showHoldingStatusBadge}
          showTemporaryHoldBadge={showTemporaryHoldBadge}
          isLoading={isLoading}
          birthDate={birthDate}
          holdingStatus={holdingStatus}
          remainingDays={remainingDays ?? 0}
          rpmDoNotCall={rpmDoNotCall}
          ccmDoNotCall={ccmDoNotCall}
          rpmRestrictionType={rpmRestrictionType}
          ccmRestrictionType={ccmRestrictionType}
          showSalesForceIntegration={showSalesForceIntegration}
          isShowEmailTag={isShowEmailTag}
          serviceDeliveryModel={serviceDeliveryModel}
          providerServiceType={providerServiceType}
          pcmStatus={pcmStatus}
          sla={sla}
          pmpmStatus={pmpmStatus}
          fullName={fullName}
          firstName={firstName ?? undefined}
          lastName={lastName ?? undefined}
          ccmClinicTime={ccmInteractionTime?.clinicTime}
          ccmTotalTime={ccmInteractionTime?.totalTime}
          rpmClinicTime={rpmInteractionTime?.clinicTime}
          rpmTotalTime={rpmInteractionTime?.totalTime}
          clinicReviewTimeSeconds={clinicReviewTimeSeconds}
          currentMonth={currentMonth}
          clinicName={clinicNameElement}
          physician={providerNameElement}
          referralTypes={referralTypes}
          rpmStatusObj={rpmStatusObj}
          ccmStatusObj={ccmStatusObj}
          riskScore={riskScore}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
          conditions={conditions?.items}
          careAgentName={careAgentName}
          conditionLoading={conditionLoading}
          patientDetailsLoading={patientDetailsLoading}
          consumablesRemainingDays={consumablesRemainingDays}
          patientTZ={patientTZ ?? ''}
          emailSubscription={patientInfo?.emailSubscription}
          apcmStatusObj={apcmStatusObj}
          onAdd={() => setManualInteractionsPopupOpen(true)}
          addLoading={isSetInteractionTimeLoading}
        />
      )}
      {isMobileDevice && (
        <CombineMobileBanner
          patientGuid={patientGuid}
          showHoldingStatusBadge={showHoldingStatusBadge}
          showTemporaryHoldBadge={showTemporaryHoldBadge}
          isLoading={isLoading}
          birthDate={birthDate}
          statusColor={statusColor}
          holdingStatus={holdingStatus}
          remainingDays={remainingDays ?? 0}
          rpmDoNotCall={rpmDoNotCall}
          ccmDoNotCall={ccmDoNotCall}
          rpmRestrictionType={rpmRestrictionType}
          ccmRestrictionType={ccmRestrictionType}
          isShowEmailTag={isShowEmailTag}
          serviceDeliveryModel={serviceDeliveryModel}
          providerServiceType={providerServiceType}
          pcmStatus={pcmStatus}
          showSalesForceIntegration={showSalesForceIntegration}
          sla={sla}
          fullName={fullName}
          clinicName={clinicNameElement}
          physician={providerNameElement}
          referralTypes={referralTypes}
          rpmStatusObj={rpmStatusObj}
          ccmStatusObj={ccmStatusObj}
          apcmStatusObj={apcmStatusObj}
          riskScore={riskScore}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
          conditions={conditions?.items}
          careAgentName={careAgentName}
          pmpmStatus={pmpmStatus}
        />
      )}
      <ManualInteractionTimeModalNew
        isOpen={manualInteractionsPopupOpen}
        toggle={() => setManualInteractionsPopupOpen(false)}
        patientGuid={patientGuid}
        module={ModuleType.RPMCCM}
        loading={isSetInteractionTimeLoading}
        submit={setManualInteractionTime}
        disableCcm={isBlockCcmModule}
        disableRpm={isBlockRpmModule}
      />
    </>
  );
};
export default CcmRpmPatientBanner;
